package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.repository.ShiftRepository
import kotlinx.datetime.plus

class ResolveAbsenceConflictsUseCase(
    private val shiftRepository: ShiftRepository,
    /** Reprices the shift for its new assignee (financial managers; best effort, see SH-12). */
    private val calculateShiftCost: CalculateShiftCostUseCase? = null
) {
    /**
     * Unassigns the person from their shifts in [workplaceId] during an approved absence: the
     * shifts become OPEN demand for someone else. Returns how many shifts were freed. SH-223: an
     * absence of part of a day ([startTime]–[endTime], "HH:mm") frees only the shifts it overlaps.
     */
    suspend operator fun invoke(
        workplaceId: String, userId: String, startDate: String, endDate: String,
        startTime: String? = null, endTime: String? = null
    ): Result<Int> = runCatching {
        val shifts = shiftRepository.getUserShiftsInWorkplace(workplaceId, userId, startDate, endDate).getOrThrow()
            .filter { overlaps(it, startDate, startTime, endTime) }
        shifts.forEach {
            shiftRepository.assignUser(it.id, userId = null, conflictMeta = null).getOrThrow()
            calculateShiftCost?.invoke(it.id)
        }
        shifts.size
    }

    /** SH-223: [absence]'s conflicts, by its dates and hours. */
    suspend operator fun invoke(workplaceId: String, absence: Absence): Result<Int> =
        invoke(workplaceId, absence.userId, absence.startDate, absence.endDate, absence.startTime, absence.endTime)

    companion object {
        /**
         * Whether [shift] clashes with time off on [date] from [startTime] to [endTime] (both
         * "HH:mm"; either missing: the whole day, every shift of the dates clashes).
         */
        fun overlaps(shift: Shift, date: String, startTime: String?, endTime: String?): Boolean {
            if (startTime.isNullOrBlank() || endTime.isNullOrBlank()) return true
            val start = shift.plannedTime.start.take(16)
            val end = shift.plannedTime.end.take(16)
            if (start.isEmpty() || end.isEmpty()) return true
            val from = "${date.take(10)}T${startTime.padStart(5, '0')}"
            val to = if (endTime <= startTime) "${nextDay(date)}T${endTime.padStart(5, '0')}" else "${date.take(10)}T${endTime.padStart(5, '0')}"
            return start < to && from < end
        }

        private fun nextDay(date: String): String =
            runCatching { kotlinx.datetime.LocalDate.parse(date.take(10)).plus(kotlinx.datetime.DatePeriod(days = 1)).toString() }
                .getOrDefault(date.take(10))
    }
}
